/**
 * Bloom post-processing: downscale → box blur → composite with screen blending.
 * Uses OffscreenCanvas (or regular canvas fallback) for the blur target.
 */
export class BloomPass {
  private blurCanvas: HTMLCanvasElement;
  private blurCtx: CanvasRenderingContext2D;
  private enabled: boolean = true;
  private intensity: number = 0.35;
  private downscale: number = 4; // blur at 1/4 resolution for performance

  constructor() {
    this.blurCanvas = document.createElement('canvas');
    this.blurCtx = this.blurCanvas.getContext('2d')!;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setIntensity(intensity: number): void {
    this.intensity = Math.max(0, Math.min(1, intensity));
  }

  /**
   * Apply bloom to the source canvas and composite back.
   * Call this after all world-space rendering is done.
   */
  apply(sourceCanvas: HTMLCanvasElement): void {
    if (!this.enabled || this.intensity < 0.01) return;

    const sw = sourceCanvas.width;
    const sh = sourceCanvas.height;
    const bw = Math.max(1, Math.floor(sw / this.downscale));
    const bh = Math.max(1, Math.floor(sh / this.downscale));

    // Resize blur canvas if needed
    if (this.blurCanvas.width !== bw || this.blurCanvas.height !== bh) {
      this.blurCanvas.width = bw;
      this.blurCanvas.height = bh;
    }

    const ctx = this.blurCtx;

    // Step 1: Downscale source into blur canvas
    ctx.clearRect(0, 0, bw, bh);
    ctx.drawImage(sourceCanvas, 0, 0, bw, bh);

    // Step 2: Apply blur via CSS filter (fast path using canvas filter)
    // Multiple passes of small blur for better quality
    const blurRadius = Math.max(2, Math.floor(bw / 40));
    ctx.filter = `blur(${blurRadius}px)`;
    ctx.drawImage(this.blurCanvas, 0, 0);
    ctx.drawImage(this.blurCanvas, 0, 0); // second pass
    ctx.filter = 'none';

    // Step 3: Composite blurred image back onto source with screen blending
    const sourceCtx = sourceCanvas.getContext('2d');
    if (!sourceCtx) return;

    sourceCtx.save();
    sourceCtx.setTransform(1, 0, 0, 1, 0, 0); // reset to device pixels
    sourceCtx.globalCompositeOperation = 'screen';
    sourceCtx.globalAlpha = this.intensity;
    sourceCtx.drawImage(this.blurCanvas, 0, 0, sw, sh);
    sourceCtx.globalAlpha = 1;
    sourceCtx.globalCompositeOperation = 'source-over';
    sourceCtx.restore();
  }
}
